import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Col, Dropdown, Input, Row, Table } from 'core/components/ui';
import { t } from 'i18next';
import styled from 'styled-components';
import type { MenuProps } from 'antd';
import { useDispatch } from 'react-redux';
import type { MyUserViewModel } from '../../services';
import { useColumns, useUpdateManyUsersRoles } from './hooks';
import type { Column, RowData } from './types';
import type { UserId } from '../../types';
import type { BaseEntity, UserRoles } from '../../base';
import { updateDocumentAccess } from '../../store/modules/documents/actions';
import type { DocumentAccessManager } from '../../hooks';

const SSearch = styled(Input.Search)`
  margin-bottom: 8px;
  width: 100%;
`;
const SDropdownButton = styled(Dropdown.Button)`
  span {
    padding-left: 4px;
    padding-right: 4px;
  }
`;

const pagination = {
  hideOnSinglePage: true,
};

interface UsersListProps {
  user: MyUserViewModel | null;
  dataSource: RowData[];
  isLoading: boolean;
  renderRoles: Column['render'];
  document?: BaseEntity;
  accessManager?: DocumentAccessManager;
  roleOptions: MenuProps['items'];
  updateUsersRole: (listIds: UserId[], role: UserRoles[]) => void;
  disabled: boolean;
}

export function UsersList({
  dataSource,
  user,
  isLoading,
  renderRoles,
  document,
  accessManager,
  updateUsersRole,
  disabled,
  roleOptions,
}: UsersListProps) {
  const dispatch = useDispatch();

  const [searchText, setSearchText] = useState('');
  const [filteredData, setFilteredData] = useState<RowData[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<UserId[]>([]);

  const { mutateAsync: updateManyUsersRoles } = useUpdateManyUsersRoles();

  useEffect(() => {
    const newFilteredData = dataSource.filter(item => {
      return item.email.includes(searchText) || item.firstName?.includes(searchText);
    });
    const newSearchData = selectedRowKeys.filter(key => {
      return newFilteredData.find(item => item.id === key);
    });

    setSelectedRowKeys(newSearchData);
    setFilteredData(newFilteredData);
  }, [dataSource, searchText]);

  const handleMenuClick: MenuProps['onClick'] = async e => {
    const isAccessSelect = document && accessManager;
    if (isAccessSelect) {
      const request = accessManager.updateForUsers(
        selectedRowKeys,
        (e.key !== 'None' ? e.key : null) as UserRoles,
      );

      dispatch(updateDocumentAccess({ id: document._id, request }));
    } else {
      const roles = e.keyPath as UserRoles[];

      const usersForUpdate = selectedRowKeys.map(key => {
        return { userID: key, roles };
      });

      updateUsersRole(selectedRowKeys, roles);

      await updateManyUsersRoles(usersForUpdate);
    }
    setSelectedRowKeys([]);
  };

  const menuProps = {
    items:
      document && roleOptions
        ? [...roleOptions, { label: 'None', value: 'None', key: 'None' }]
        : roleOptions,
    onClick: handleMenuClick,
  };

  const columns = useColumns({
    searchText,
    user,
    renderRoles,
  });

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys as UserId[]);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    selections: disabled
      ? undefined
      : [Table.SELECTION_ALL, Table.SELECTION_INVERT, Table.SELECTION_NONE],
    getCheckboxProps: (item: RowData) => {
      return {
        disabled: (!!user && user.id === item.id) || disabled,
      };
    },
  };

  const locale = useMemo(() => {
    return {
      selectionAll: t('Users.SelectAllUsers'),
      selectInvert: t('Users.InvertSelectUsers'),
      selectNone: t('Users.DeselectAllUsers'),
    };
  }, []);

  return (
    <>
      <Row gutter={4}>
        <Col span={20}>
          <SSearch placeholder={t('Users.SearchPlaceholder')} allowClear onSearch={setSearchText} />
        </Col>
        <Col span={4}>
          <SDropdownButton menu={menuProps} disabled={selectedRowKeys.length === 0 || disabled}>
            {t('Users.ModalAssignAs')}
          </SDropdownButton>
        </Col>
      </Row>
      <Table
        rowKey="id"
        size="small"
        rowSelection={rowSelection}
        columns={columns}
        pagination={pagination}
        loading={isLoading}
        dataSource={filteredData}
        locale={locale}
      />
    </>
  );
}
